// src/app/modules/Members/pages/components/EditSpaceModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import * as actions from '../../../Dashbord/_redux/dashboard/Actions';
import { Info, Save, X } from 'lucide-react';
import './InviteMembersModal.scss';
import { useOrgConfig } from '../../../../../hooks/useOrgConfig';

export function EditSpaceModal({ show, onHide, space, organizationId, onSuccess }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { hasFeature, getLabel } = useOrgConfig();

  const [formData, setFormData] = useState({
    name: '',
    type: 'unit',
    floor: '',
    metadata: {},
  });

  const spaceTypeOptions = [
    { value: 'unit',       label: 'Unit' },
    { value: 'house',      label: 'House' },
    { value: 'office',     label: 'Office' },
    { value: 'room',       label: 'Room' },
    { value: 'shop',       label: 'Shop' },
    { value: 'department', label: 'Department' },
    { value: 'ward',       label: 'Ward' },
    { value: 'other',      label: 'Other' },
  ];

  // Populate form when space changes
  useEffect(() => {
    if (space && show) {
      setFormData({
        name:     space.name     || '',
        type:     space.type     || 'unit',
        floor:    space.floor    || '',
        metadata: space.metadata || {},
      });
    }
  }, [space, show]);

  // Reset on close
  useEffect(() => {
    if (!show) {
      setFormData({ name: '', type: 'unit', floor: '', metadata: {} });
      setLoading(false);
    }
  }, [show]);

  const shouldShowFloor = ['unit', 'house', 'office', 'shop', 'ward'].includes(formData.type);

  async function handleSubmit(e) {
    e.preventDefault();
    // console.log(space); return;
    if (!space?.space_id && !space?.id) return;
    setLoading(true);
    try {
      const payload = {
        ...formData,
        space_id: space.space_id || space.id,
        floor: shouldShowFloor ? formData.floor : undefined,
      };

      await dispatch(
        actions.addData(
          'update_space',
          payload,
          organizationId
        //   space.space_id || space.id
        )
      );

      if (onSuccess) onSuccess();
      onHide();
    } catch (error) {
      console.error('Error updating space:', error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="create-space-modal"
      backdrop={loading ? 'static' : true}
    >
      <form onSubmit={handleSubmit}>
        <div className="modal-header">
          <h5 className="modal-title">Edit {getLabel('spaces')}</h5>
          <button type="button" className="close" onClick={onHide} disabled={loading}>
            <span>&times;</span>
          </button>
        </div>

        <div className="modal-body">
          {/* Space Name */}
          <div className="form-group">
            <label className="form-label">{getLabel('spaces')} Name / Number</label>
            <input
              required
              className="form-control"
              placeholder="e.g. Apartment 4B or Marketing Dept"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              disabled={loading}
            />
          </div>

          {/* Space Type */}
          <div className="form-group">
            <select
              className="form-control"
              value={formData.type}
              onChange={e => setFormData({ ...formData, type: e.target.value })}
              disabled={loading}
            >
              {spaceTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Floor field — conditional */}
          {shouldShowFloor && (
            <div className="form-group animate__animated animate__fadeIn">
              <label className="form-label">Floor / Level (Optional)</label>
              <input
                className="form-control"
                placeholder="e.g. Ground, 2nd Floor, Penthouse"
                value={formData.floor}
                onChange={e => setFormData({ ...formData, floor: e.target.value })}
                disabled={loading}
              />
            </div>
          )}

          <div className="metadata-info">
            <Info size={14} className="mr-2" />
            <small className="text-muted">
              Changes will apply to all members assigned to this space.
            </small>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-light"
            onClick={onHide}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !formData.name}
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <>
                <Save size={14} className="mr-1" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}